<script setup>
import {
  Switch,
  SwitchDescription,
  SwitchGroup,
  SwitchLabel,
} from "@headlessui/vue";

import { useI18n } from "vue-i18n";
import { defineEmits, ref } from "vue";

const emit = defineEmits(["close", "cancel"]);
const props = defineProps({ application: { type: String, required: true } });

const switchOne = ref(false);
const selection = ref("0");

const error = ref(null);
const showError = ref(false);
const { t } = useI18n();
const cancel = () => {
  emit("close");
};
const agree = async () => {
  if (selection.value === "0" || !switchOne.value) {
    showError.value = true;
    if (selection.value === "0") {
      error.value = t("consent.reapply.selection");
    } else {
      error.value = t("consent.reapply.error");
    }
    return true;
  }
  showError.value = false;
  error.value = null;

  try {
    const { data } = await axios.post(
      `/applications/${props.application}/re-apply`,
      {
        reason: selection.value,
      }
    );
    window.location.href = data.redirect_url;
  } catch (e) {
    error.value = e.response.data.message;
    showError.value = true;
    window.location.href = "/dashboard";
  }
};
</script>
<template>
  <div class="">
    <div class="flex flex-col space-y-4">
      <h2 class="text-2xl font-bold text-vulcan">
        {{ t("consent.heading") }}
      </h2>

      <SwitchGroup
        as="div"
        class="px-4 py-4 py-5 overflow-hidden bg-white shadow sm:p-6 sm:px-6 sm:rounded-md"
      >
        <div class="flex flex-col pb-[20px] border-b-[1px] border-gray-200">
          <label for="selection">
            {{ t("consent.reapply.reason") }}
          </label>
          <select v-model="selection" name="selection" class="w-full">
            <option value="0">{{ t("consent.reapply.selection_zero") }}</option>
            <option value="1">
              {{ t("consent.reapply.selection_one") }}
            </option>
            <option value="2">
              {{ t("consent.reapply.selection_two") }}
            </option>
          </select>
        </div>
        <SwitchLabel
          as="h3"
          class="text-[18px] font-semibold leading-6 text-[#1B1C20] pt-[20px]"
          passive
        >
          {{ t("consent.reapply.heading") }}
        </SwitchLabel>
        <div class="flex-col mt-2 sm:flex sm:items-start sm:justify-between">
          <div class="max-w-xl text-[14px] text-[#1B1C20] space-y-3">
            <p>{{ t("consent.reapply.row1") }}</p>
            <p>{{ t("consent.reapply.row2") }}</p>
            <p>{{ t("consent.reapply.row3") }}</p>
            <p>{{ t("consent.reapply.row4") }}</p>
          </div>
          <div
            class="mt-5 space-x-4 sm:flex-shrink-0 sm:flex sm:flex-row sm:items-center"
          >
            <Switch
              v-model="switchOne"
              :class="[
                switchOne
                  ? 'bg-green-500 focus:ring-green-500'
                  : 'bg-gray-200 focus:ring-gray-200',
                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2',
              ]"
            >
              <span
                aria-hidden="true"
                :class="[
                  switchOne ? 'translate-x-5' : 'translate-x-0',
                  'inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                ]"
              />
            </Switch>
            <p>{{ t("consent.check") }}</p>
          </div>
        </div>
      </SwitchGroup>
    </div>

    <!-- Errors -->
    <p class="py-4 text-sm font-bold text-red-500" v-if="showError">
      {{ error }}
    </p>

    <!--Action -->
    <div class="flex flex-row justify-end p-4 space-x-4">
      <button
        @click="cancel"
        class="px-4 py-1 text-white bg-red-500 rounded-md"
      >
        {{ t("consent.cancel") }}
      </button>
      <button
        @click="agree"
        class="px-4 py-1 text-white bg-green-500 rounded-md"
      >
        {{ t("consent.button") }}
      </button>
    </div>
  </div>
</template>
